import React from 'react';
import BarLoader from 'react-spinners/BarLoader';
import apiFetch from '../../src/js/fetch';
import Activity from '../../src/js/activities/debug';

function SampleTimeline({ samples, hoveredIndex, onHover }) {
  const svgRef = React.useRef(null);

  const width = 800;
  const height = 32;
  const padX = 10;
  const total = samples.length;

  const cx = (i) => padX + (i / (total - 1)) * (width - 2 * padX);

  const times = samples.map((s) => s.time_in_seconds ?? s.elapsed_time).filter((v) => v != null);
  if (times.length === 0) return null;

  const minT = times[0];
  const maxT = times[times.length - 1];
  const duration = maxT - minT || 1;

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      : `${m}:${String(s).padStart(2, '0')}`;
  };

  const tickCount = 6;
  const ticks = Array.from({ length: tickCount }, (_, k) => {
    const t = Math.round((k / (tickCount - 1)) * duration);
    const i = Math.round((k / (tickCount - 1)) * (total - 1));
    return { t, i };
  });

  const handleMouseMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) / rect.width) * width;
    const targetI = Math.round(((mouseX - padX) / (width - 2 * padX)) * (total - 1));
    const clamped = Math.max(0, Math.min(total - 1, targetI));
    onHover(clamped);
  };

  const activeTime = hoveredIndex != null ? (samples[hoveredIndex]?.time_in_seconds ?? samples[hoveredIndex]?.elapsed_time) - minT : null;

  return (
    <div className='mb-4'>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        style={{ display: 'block' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => onHover(null)}
      >
        <line x1={padX} y1={10} x2={width - padX} y2={10} stroke="#ccc" strokeWidth="1" />
        {ticks.map(({ t, i }) => (
          <g key={i}>
            <line x1={cx(i)} y1={7} x2={cx(i)} y2={13} stroke="#999" strokeWidth="1" />
            <text x={cx(i)} y={26} fontSize="10" textAnchor="middle" fill="#999">
              {formatTime(t)}
            </text>
          </g>
        ))}
        {hoveredIndex != null && (
          <>
            <line x1={cx(hoveredIndex)} y1={4} x2={cx(hoveredIndex)} y2={16} stroke="#333" strokeWidth="1.5" />
            {activeTime != null && (
              <text
                x={Math.min(cx(hoveredIndex) + 6, width - 70)}
                y={8}
                fontSize="10"
                fill="#333"
              >
                {formatTime(activeTime)}
              </text>
            )}
          </>
        )}
      </svg>
    </div>
  );
}

function SampleChart({ samples, field, label, color = '#2C3232', unit = '', hoveredIndex, onHover }) {
  const svgRef = React.useRef(null);

  const width = 800;
  const height = 120;
  const padX = 10;
  const padY = 14;

  const points = samples
    .map((s, i) => ({ v: s[field], i }))
    .filter(({ v }) => v != null && !isNaN(v));

  if (points.length === 0) return null;

  const values = points.map((p) => p.v);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const total = samples.length;

  const cx = (i) => padX + (i / (total - 1)) * (width - 2 * padX);
  const cy = (v) => padY + (1 - (v - min) / range) * (height - 2 * padY);

  const polyline = points.map(({ v, i }) => `${cx(i)},${cy(v)}`).join(' ');

  const handleMouseMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) / rect.width) * width;
    const targetI = Math.round(((mouseX - padX) / (width - 2 * padX)) * (total - 1));
    const nearest = points.reduce((a, b) =>
      Math.abs(a.i - targetI) <= Math.abs(b.i - targetI) ? a : b
    );
    onHover(nearest.i);
  };

  const activePoint = hoveredIndex != null ? points.find((p) => p.i === hoveredIndex) : null;

  return (
    <div className='mb-4'>
      <p className='mini-text mb-1'>{label}</p>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        style={{ display: 'block' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => onHover(null)}
      >
        <polyline points={polyline} fill='none' stroke={color} strokeWidth='1.5' />
        {activePoint && (
          <>
            <circle cx={cx(activePoint.i)} cy={cy(activePoint.v)} r={4} fill={color} />
            <text
              x={Math.min(cx(activePoint.i) + 8, width - 70)}
              y={Math.max(cy(activePoint.v) - 6, 14)}
              fontSize='11'
              fill={color}
            >
              {Math.round(activePoint.v)}{unit}
            </text>
          </>
        )}
      </svg>
    </div>
  );
}

class ActivityShow extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      state: props.state,
      activity: props.activity,
      segments: [],
      segmentSearch: '',
      segmentId: '',
      loaded: true,
      activitySamples: null,
      hoveredSampleIndex: null,

      lat: '',
      lng: '',
    };

    this.initMap = this.initMap.bind(this);
  }

  componentDidMount() {
    const { state, activity } = this.state;

    console.log({ state, activity });

    if (state === 'completed') {
      // init map
      this.initMap(activity);
    } else {
      alert('Activity is not in completed state');
    }
  }

  async initMap(activity) {
    let coords = null;
    if (activity.sample_file) {
      // Fetch the coordinates.
      const d = await apiFetch(activity.sample_file);
      if (d && d.samples) {
        coords = d.samples;
      }
    }

    if (coords) {
      this.activitySamples = coords;
      this.setState({ activitySamples: coords });

      console.log({ activitySamples: this.activitySamples });

      // init map
      this.mapHandler = new Activity({
        coordinates: this.activitySamples,
        segmentType: activity.segment_type,
        maxZoom: 25,
        polylineOptions: {
          color: '#2C3232',
          weight: 1,
        },
      });

      this.mapHandler.onHoverSample = this.onHoverSample;

      // Add debug markers for the activity samples
      this.mapHandler.addDebugMarkersToMap(this.activitySamples);
    }
  }

  componentWillUnmount() {
    // stop polling
    console.log('unmounting');
  }

  onChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });

    if (name === 'segmentSearch') {
      this.searchSegments(value);
    }

    if (name === 'segmentId') {
      this.addSegmentToMap(value);
    }
  };

  searchSegments = async (value) => {
    console.log('searching segments:', value);

    this.setState({ loaded: false });
    try {
      const segments = await apiFetch(`/api/segments/search?search=${value}`);
      console.log({ segments });

      this.setState({ segments });

      // if (segments.length) {
      //     this.mapHandler.addSegmentsToMap(segments);
      //     this.mapHandler.flyTo([segments[0].stop_lat, segments[0].stop_lng])
      // }
    } catch (error) {
      console.log({ error });
    }
    this.setState({ loaded: true });
  };

  addSegmentToMap = async (segmentId) => {
    console.log('add segment to map:', segmentId);

    const segment = this.state.segments.find(
      (s) => s.id === parseInt(segmentId)
    );

    let coords = null;
    if (segment.sample_file) {
      // Fetch the coordinates.
      const d = await apiFetch(segment.sample_file);
      if (d && d.coordinates) {
        coords = d.coordinates;
      }
    }

    if (coords) {
      console.log('Adding segment to map:', segmentId, coords);
      // Add debug markers for the activity samples
      this.mapHandler.addDebugMarkersToMap(coords, 'segment', segment.name);
    }
  };

  onHoverSample = (index) => {
    this.setState({ hoveredSampleIndex: index });
    if (this.mapHandler) {
      if (index != null) {
        this.mapHandler.highlightSample(index);
      } else {
        this.mapHandler.clearHighlight();
      }
    }
  };

  addToMap = () => {
    const { lat, lng } = this.state;

    this.mapHandler.addMarker([parseFloat(lat), parseFloat(lng)], {
      color: 'yellow',
    });
    this.mapHandler.flyTo([parseFloat(lat), parseFloat(lng)]);

    this.setState({ lat: '', lng: '' });
  };

  render() {
    const { state, segments, segmentSearch, segmentId, loaded, lat, lng, activitySamples, hoveredSampleIndex } =
      this.state;

    return (
      <>
        <h2 className='mini-label font-medium'>Activity</h2>
        {state !== 'completed' && (
          <div
            className={`inline-block rounded p-8pt mb-24pt ${
              state === 'completed'
                ? 'bg-primary'
                : state === 'processing'
                ? 'bg-tertiary'
                : 'bg-error'
            }`}
          >
            <p className='mb-0 text-body-inverted'>
              Status:{' '}
              {state === 'error'
                ? `${state} - will try sync again at a later point.`
                : state}
            </p>
          </div>
        )}

        <div>
          <label className='input-label block'>Compare segment</label>
          <input
            className='input mb-4 w-full'
            type='text'
            name='segmentSearch'
            id='segment_name'
            value={segmentSearch}
            onChange={this.onChange}
            placeholder='Search for a segment'
          />

          <select
            className='input mb-4 pr-8'
            name='segmentId'
            value={segmentId}
            onChange={this.onChange}
            id='segment_id'
          >
            <option value=''>Select a segment</option>
            {segments.map((segment, i) => (
              <option key={i} value={segment.id}>
                {segment.name}
              </option>
            ))}
          </select>
          {!loaded && (
            <div className='mb-4'>
              <BarLoader />
            </div>
          )}
        </div>
        <div className='mb-4'>
          <label className='input-label block'>Add custom debug marker</label>
          <input
            className='input mb-4 w-full'
            type='text'
            name='lat'
            id='lat'
            value={lat}
            placeholder='Lat: 55.0000000'
            onChange={this.onChange}
          />
          <input
            className='input mb-4 w-full'
            type='text'
            name='lng'
            id='lng'
            value={lng}
            placeholder='Lng: 10.0000000'
            onChange={this.onChange}
          />
          <button className='button dark' onClick={this.addToMap}>
            Add to map
          </button>
        </div>

        {state === 'completed' ? (
          <>
            <div className='small-body-text mb-4'>
              <p className='mb-1'>Blue dots: Activity coordinates</p>
              <p className='mb-1'>
                Green dots: Calculated coordinates (added to the activity when
                analyzed if device was using "Smart" recording)
              </p>
              <p className='mb-3'>Red dots: Segment coordinates</p>
              <p className='mb-3'>
                Orange dots: Start of a 100m anomaly check fail (running)
              </p>
              <p className='mb-3'>
                Purple dots: End of a 100m anomaly check fail (running)
              </p>

              <p className='mini-text text-text-grey-600'>
                To measure between points: Click the ruler on the right to
                corner (in the map). You can then click where you want, and
                click another point to see the distance. Double 'escape' stops
                the measurement.
              </p>
            </div>
            <div id='map' className='map'></div>
            {activitySamples && (
              <div className='mt-4'>
                <SampleChart
                  samples={activitySamples}
                  field='power'
                  label='Power (W)'
                  color='#E85D04'
                  unit='W'
                  hoveredIndex={hoveredSampleIndex}
                  onHover={this.onHoverSample}
                />
                <SampleChart
                  samples={activitySamples}
                  field='altitude'
                  label='Altitude (m)'
                  color='#2C3232'
                  unit='m'
                  hoveredIndex={hoveredSampleIndex}
                  onHover={this.onHoverSample}
                />
                <SampleTimeline
                  samples={activitySamples}
                  hoveredIndex={hoveredSampleIndex}
                  onHover={this.onHoverSample}
                />
              </div>
            )}
            {/* <a href={activity.create_segment_url} className="button">Create a segment</a> */}
          </>
        ) : null}

        {state === 'processing' || (state === 'error' && <BarLoader />)}
        {/* {state === 'completed' && activity.segment_efforts.length > 0 &&
                    <div className="p-16pt">
                        <h4>Segment efforts</h4>
                        <ul>
                            {activity.segment_efforts.map(s => {
                                return (
                                    <li key={`segment_effort_${s.id}`}>{s.name}: {s.duration_in_seconds} seconds. <a className="text-text-accent" href={s.url}>View segment</a></li>
                                )
                            })}
                        </ul>
                    </div>
                } */}
      </>
    );
  }
}
export default ActivityShow;
